import { useState } from "react";
import { useTranslation } from "react-i18next";
import QuoteModal from "../components/QuoteModal";

/* ===================== HERO ===================== */
function HeroWithImage({ onOpenQuote, onOpenGallery, t }) {
  const desktop = "/header-desktop.jpg";
  const tablet = "/header-tablet.jpg";
  const mobile = "/header-mobile.jpg";
  const fallback = "/header.jpg"; // 1500x400

  return (
    <section className="relative border-t border-redglass-light/60">
      {/* Image area: taller on mobile so the centered card has room */}
      <div className="relative h-[320px] sm:h-[340px] md:h-[380px] lg:h-[420px] xl:h-[460px] overflow-hidden">
        <picture>
          <source media="(min-width: 1024px)" srcSet={desktop} />
          <source media="(min-width: 640px)" srcSet={tablet} />
          <source media="(max-width: 639px)" srcSet={mobile} />
          <img
            src={fallback}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
        </picture>

        {/* Readability overlay */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(0deg, rgba(0,0,0,0.35), rgba(0,0,0,0.22))",
          }}
        />

        {/* Centered content card */}
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <div className="w-full max-w-md rounded-brand border border-white/40 bg-white/15 backdrop-blur-md p-4 md:p-5 shadow-glass">
            <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight text-white drop-shadow">
              {t("hero.title")}
            </h1>
            <p className="mt-2 h-1 w-14 sm:w-16 bg-redglass-primary/70 rounded-full" />
            <p className="mt-3 text-base sm:text-lg text-white/95 drop-shadow">
              {t("hero.subtitle")}
            </p>

            <div className="mt-4 flex flex-wrap gap-2 sm:gap-3">
              <button
                onClick={onOpenQuote}
                className="rounded-brand px-4 py-2 sm:px-5 sm:py-3 text-sm sm:text-base font-medium text-white bg-redglass-primary hover:bg-redglass-dark shadow-brand ring-1 ring-redglass-ring/40 transition-colors"
              >
                {t("cta.primary")}
              </button>
              <button
                onClick={onOpenGallery}
                className="rounded-brand px-4 py-2 sm:px-5 sm:py-3 text-sm sm:text-base font-medium bg-white/80 hover:bg-white border border-white/80 backdrop-blur text-redglass-primary"
              >
                {t("gallery.view")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Benefit({ title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="mt-1 h-5 w-5 text-redglass-primary"
        fill="currentColor"
      >
        <path d="M12 2l7 3v6c0 5-3.5 9.5-7 11-3.5-1.5-7-6-7-11V5l7-3z" />
      </svg>
      <div>
        <p className="font-medium text-redglass-ink">{title}</p>
        <p className="text-sm text-gray-600">{desc}</p>
      </div>
    </div>
  );
}

function Step({ index, title, desc }) {
  return (
    <div className="relative pl-10">
      <div className="absolute left-0 top-0">
        <div className="h-8 w-8 rounded-full bg-redglass-primary text-white grid place-items-center text-sm">
          {index}
        </div>
      </div>
      <h3 className="font-semibold text-redglass-ink">{title}</h3>
      <p className="text-gray-600">{desc}</p>
    </div>
  );
}

/* ===================== SERVICES ICONS ===================== */
function IconRepair({ className = "" }) {
  // Recondiţionări parbrize — crack + resin drop
  return (
    <svg
      viewBox="0 0 48 48"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="5" y="8" width="38" height="28" rx="3" />
      <path d="M24 12v6l-4 3 6 4-2 5" />
      <path
        d="M33 30c0 3-4 6-4 6s-4-3-4-6a4 4 0 1 1 8 0z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  );
}
function IconInstall({ className = "" }) {
  // Montări parbrize — windshield held by two hands
  return (
    <svg
      viewBox="0 0 48 48"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="6" y="8" width="36" height="20" rx="3" />
      <path d="M10 36l6-6m16 0l6 6" />
      <path d="M16 30v6c0 2-2 4-4 4H9m30 0h-3c-2 0-4-2-4-4v-6" />
    </svg>
  );
}
function IconReplace({ className = "" }) {
  // Schimburi parbrize — swap arrows around a windshield
  return (
    <svg
      viewBox="0 0 48 48"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="8" y="10" width="32" height="18" rx="3" />
      <path d="M12 34c0 0 4 4 12 4s12-4 12-4" />
      <path d="M9 24l-5-5 5-5" />
      <path d="M39 14l5 5-5 5" />
    </svg>
  );
}
function IconSell({ className = "" }) {
  // Vânzări parbrize — price tag on a windshield
  return (
    <svg
      viewBox="0 0 48 48"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="7" y="8" width="34" height="20" rx="3" />
      <path d="M30 30l6 6 7-7-6-6-7 7z" />
      <circle cx="38.5" cy="28.5" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

/* ===================== SERVICES SECTION ===================== */
function ServicesSection({ t }) {
  const items = [
    { key: "repair", title: t("services.items.repair"), Icon: IconRepair },
    { key: "install", title: t("services.items.install"), Icon: IconInstall },
    { key: "replace", title: t("services.items.replace"), Icon: IconReplace },
    { key: "sell", title: t("services.items.sell"), Icon: IconSell },
  ];

  return (
    <section className="bg-white border-t border-redglass-light/60">
      <div className="max-w-6xl mx-auto px-4 py-10">
        <h2 className="text-2xl sm:text-3xl font-bold text-redglass-ink">
          {t("services.title")}
        </h2>
        <p className="mt-1 h-1 w-14 bg-redglass-primary/40 rounded-full" />
        <div className="mt-7 grid gap-4 sm:gap-5 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {items.map(({ key, title, Icon }) => (
            <div
              key={key}
              className="rounded-2xl border bg-white/70 backdrop-blur p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0">
                  <Icon className="h-10 w-10 text-redglass-primary" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-redglass-ink">
                    {title}
                  </h3>
                  {/* Optional: add descriptions later with t(`services.desc.${key}`) */}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===================== ADDRESS ICONS ===================== */
function IconClock({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
function IconMapPin({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M12 22s7-7 7-12a7 7 0 1 0-14 0c0 5 7 12 7 12z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
function IconPhone({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.8 19.8 0 0 1 3.1 5.18 2 2 0 0 1 5.11 3h3a2 2 0 0 1 2 1.72c.12.86.31 1.7.57 2.5a2 2 0 0 1-.45 2.11L9 10a16 16 0 0 0 5 5l.67-1.23a2 2 0 0 1 2.11-.45c.8.26 1.64.45 2.5.57A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

/* ===================== ADDRESS SECTION ===================== */
function AddressSection() {
  const { t } = useTranslation("common");
  const addressText = "Strada Bruxelles Nr. 13, Dumbrăvița, Timiș, 307160";
  const mapsHref =
    "https://www.google.com/maps/search/?api=1&query=" +
    encodeURIComponent(addressText);
  const phone = "+40000000000";

  return (
    <section className="bg-white border-t border-redglass-light/60">
      <div className="max-w-6xl mx-auto px-4 py-10">
        <h2 className="text-2xl sm:text-3xl font-bold text-redglass-ink">
          {t("contact.title")}
        </h2>
        <p className="mt-1 h-1 w-14 bg-redglass-primary/40 rounded-full" />

        <div className="mt-7 grid gap-4 sm:gap-5 md:gap-6 grid-cols-1 md:grid-cols-3">
          {/* Program */}
          <div className="rounded-2xl border bg-white/70 backdrop-blur p-5">
            <div className="flex items-start gap-3">
              <IconClock className="h-6 w-6 text-redglass-primary" />
              <div>
                <p className="text-sm text-redglass-primary/80 font-medium">
                  {t("contact.hours.label")}
                </p>
                <p className="text-redglass-ink font-semibold mt-0.5">
                  {t("contact.hours.value")}
                </p>
              </div>
            </div>
          </div>

          {/* Adresa */}
          <div className="rounded-2xl border bg-white/70 backdrop-blur p-5">
            <div className="flex items-start gap-3">
              <IconMapPin className="h-6 w-6 text-redglass-primary" />
              <div>
                <p className="text-sm text-redglass-primary/80 font-medium">
                  {t("contact.address.label")}
                </p>
                <p className="text-redglass-ink font-semibold mt-0.5">
                  {t("contact.address.street")}
                </p>
                <p className="text-gray-700">{t("contact.address.zip")}</p>
                <a
                  href={mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="
    mt-3
    group inline-flex items-center gap-2
    rounded-full px-5 py-2.5
    bg-redglass-primary text-white
    shadow-brand ring-1 ring-redglass-ring/40
    hover:bg-redglass-dark active:translate-y-px
    transition focus:outline-none focus-visible:ring-2 focus-visible:ring-redglass-primary/60
  "
                  aria-label={t("contact.address.mapsCta")}
                >
                  {/* leading icon (map pin) */}
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    className="h-5 w-5 -ml-1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 22s7-7 7-12a7 7 0 1 0-14 0c0 5 7 12 7 12z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>

                  <span className="font-medium">
                    {t("contact.address.mapsCta")}
                  </span>

                  {/* trailing external-link chevron (optional) */}
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    className="h-5 w-5 transition-transform group-hover:translate-x-0.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M7 17l10-10M14 7h3v3" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Telefon */}
          <div className="rounded-2xl border bg-white/70 backdrop-blur p-5">
            <div className="flex items-start gap-3">
              <IconPhone className="h-6 w-6 text-redglass-primary" />
              <div>
                <p className="text-sm text-redglass-primary/80 font-medium">
                  {t("contact.phone.label")}
                </p>
                <a
                  href={`tel:${phone}`}
                  className="text-redglass-ink font-semibold mt-0.5 hover:text-redglass-primary transition"
                >
                  {phone}
                </a>
                <p className="text-gray-600 text-sm">
                  {t("contact.phone.note")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===================== PAGE ===================== */
export default function Home({ setPage }) {
  const { t } = useTranslation("common");
  const [openModal, setOpenModal] = useState(false);

  return (
    <main>
      {/* HERO */}
      <HeroWithImage
        t={t}
        onOpenQuote={() => setOpenModal(true)}
        onOpenGallery={() => setPage("gallery")}
      />

      {/* SERVICII */}
      <ServicesSection t={t} />

      {/* BENEFITS */}
      <section className="bg-white border-t border-redglass-light/60">
        <div className="max-w-6xl mx-auto px-4 py-8 grid gap-6 md:grid-cols-2">
          <Benefit
            title={t("benefits.fast.title")}
            desc={t("benefits.fast.desc")}
          />
          <Benefit
            title={t("benefits.cert.title")}
            desc={t("benefits.cert.desc")}
          />
        </div>
      </section>

      {/* ADRESĂ / CONTACT */}
      <AddressSection />

      {/* PROCESS */}
      <section className="bg-white border-t border-redglass-light/60">
        <div className="max-w-6xl mx-auto px-4 py-12 grid gap-8 md:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold text-redglass-ink">
              {t("process.title")}
            </h2>
            <p className="mt-2 text-gray-700">{t("process.subtitle")}</p>
            <div className="mt-6 space-y-6">
              <Step
                index={1}
                title={t("process.steps.0.title")}
                desc={t("process.steps.0.desc")}
              />
              <Step
                index={2}
                title={t("process.steps.1.title")}
                desc={t("process.steps.1.desc")}
              />
              <Step
                index={3}
                title={t("process.steps.2.title")}
                desc={t("process.steps.2.desc")}
              />
              <Step
                index={4}
                title={t("process.steps.3.title")}
                desc={t("process.steps.3.desc")}
              />
            </div>
          </div>

          {/* RIGHT: Warranty + Coverage only */}
          <div className="p-0 md:p-2">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border p-5 bg-white">
                <p className="text-sm text-redglass-primary/80">
                  {t("stats.warranty.label")}
                </p>
                <p className="mt-1 text-2xl font-bold text-redglass-ink">
                  {t("stats.warranty.value")}
                </p>
                <p className="text-gray-600">{t("stats.warranty.desc")}</p>
              </div>
              <div className="rounded-2xl border p-5 bg-white">
                <p className="text-sm text-redglass-primary/80">
                  {t("stats.area.label")}
                </p>
                <p className="mt-1 text-2xl font-bold text-redglass-ink">
                  {t("stats.area.value")}
                </p>
                <p className="text-gray-600">{t("stats.area.desc")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* MODAL */}
      <QuoteModal open={openModal} onClose={() => setOpenModal(false)} />
    </main>
  );
}
